import {
  Checkbox,
  Container,
  Group,
  Select,
  Stack,
  Text,
  Title,
} from '@mantine/core'
import { useRef } from 'react'
import { DailyBusinessHours } from '../../../models/DailyBusinessHours'
import { BUSINESSTIMES, DAYSOFWEEK } from '../../../utils/constants'

interface BusinessHoursStepProps {
  hours: Record<string, DailyBusinessHours>
  setHours: (hours: Record<string, DailyBusinessHours>) => void
  autoPopulateOnFirstChange?: boolean
}
export default function BusinessHoursStep(props: BusinessHoursStepProps) {
  const { hours, setHours, autoPopulateOnFirstChange = false } = props
  const hasAutoPopulatedOpen = useRef(false)
  const hasAutoPopulatedClose = useRef(false)

  const handleTimeChange = (
    day: string,
    field: 'open' | 'close',
    value: string,
  ) => {
    // Only auto-populate if enabled, it's the first day, and not yet auto-populated for that field
    if (autoPopulateOnFirstChange && day === DAYSOFWEEK[0]) {
      if (field === 'open' && !hasAutoPopulatedOpen.current && value) {
        const populated = DAYSOFWEEK.reduce((acc, d) => {
          acc[d] = { ...hours[d], open: value }
          return acc
        }, {} as Record<string, DailyBusinessHours>)
        setHours(populated)
        hasAutoPopulatedOpen.current = true
        return
      }
      if (field === 'close' && !hasAutoPopulatedClose.current && value) {
        const populated = DAYSOFWEEK.reduce((acc, d) => {
          acc[d] = { ...hours[d], close: value }
          return acc
        }, {} as Record<string, DailyBusinessHours>)
        setHours(populated)
        hasAutoPopulatedClose.current = true
        return
      }
    }
    setHours({
      ...hours,
      [day]: { ...hours[day], [field]: value },
    })
  }

  const handleClosedChange = (day: string, checked: boolean) => {
    setHours({
      ...hours,
      [day]: { ...hours[day], closed: checked },
    })
  }

  return (
    <Container w='100%'>
      <Title ta='center' mb='lg'>
        Business Hours
      </Title>

      <Stack gap={25} visibleFrom='sm'>
        {DAYSOFWEEK.map((day) => (
          <Group align='center' w='100%' gap={70} justify='center' key={day}>
            <Stack gap={0} align='flex-start' w={80}>
              <Text mb={8} fw={600}>
                {day}
              </Text>
              <Checkbox
                label='Closed'
                checked={hours[day].closed}
                onChange={(event) =>
                  handleClosedChange(day, event.currentTarget.checked)
                }
              />
            </Stack>
            <Select
              label='Opens at'
              value={hours[day].open}
              onChange={(value) =>
                handleTimeChange(day, 'open', value || '9:00 AM')
              }
              data={BUSINESSTIMES}
              disabled={hours[day].closed}
              placeholder='Select opening time'
            />
            <Select
              label='Closes at'
              value={hours[day].close}
              onChange={(value) =>
                handleTimeChange(day, 'close', value || '5:00 PM')
              }
              data={BUSINESSTIMES}
              disabled={hours[day].closed}
              placeholder='Select closing time'
            />
          </Group>
        ))}
      </Stack>
      <Stack gap={20} hiddenFrom='sm'>
        {DAYSOFWEEK.map((day) => (
          <Stack
            align='flex-start'
            w='100%'
            justify='flex-start'
            key={day}
            gap={8}
          >
            <Stack gap={0} align='flex-start' w={80}>
              <Text mb={8} fw={600}>
                {day}
              </Text>
              <Checkbox
                label='Closed'
                checked={hours[day].closed}
                onChange={(event) =>
                  handleClosedChange(day, event.currentTarget.checked)
                }
              />
            </Stack>
            <Group grow w='100%' gap='md'>
              <Select
                label='Opens at'
                value={hours[day].open}
                onChange={(value) =>
                  handleTimeChange(day, 'open', value || '9:00 AM')
                }
                data={BUSINESSTIMES}
                disabled={hours[day].closed}
                placeholder='Select opening time'
              />
              <Select
                label='Closes at'
                value={hours[day].close}
                onChange={(value) =>
                  handleTimeChange(day, 'close', value || '5:00 PM')
                }
                data={BUSINESSTIMES}
                disabled={hours[day].closed}
                placeholder='Select closing time'
              />
            </Group>
          </Stack>
        ))}
      </Stack>
    </Container>
  )
}
